import request from '@/utils/request'

/**
 * 订舱订单API模块
 * 包含个人订单、商务订单、拒绝原因查询、状态变更、费用预估、保存提交等接口
 */
const orderApi = {
    /**
     * 个人订舱订单列表（分页）
     * @param {Object} data - 查询条件 {current, size, orderStatus, pol, pod, createTimeStart, createTimeEnd}
     */
    queryMyOrderList(data) {
        return request({url: '/service/order/mylist', method: 'post', data})
    },

    /**
     * 个人订单按状态统计数量
     * 返回 key=状态值(1-6), value=该状态的订单数量
     */
    countMyStatus() {
        return request({url: '/service/order/countMyStatus', method: 'get'})
    },

    /**
     * 商务订单列表（分页）
     * @param {Object} data - 查询条件 {current, size, orderStatus, pol, pod, createBy, createTimeStart, createTimeEnd}
     */
    businessList(data) {
        return request({url: '/service/order/businessList', method: 'post', data})
    },

    /** 商务订单列表（菲律宾/旧版商务中心别名，同 businessList，排除西哈努克） */
    queryBusinessOrderList(data) {
        return request({url: '/service/order/businessList', method: 'post', data})
    },

    /** 西哈努克商务订单列表（仅目的港 KHKOS） */
    businessListSihanouk(data) {
        return request({url: '/service/order/businessListSihanouk', method: 'post', data})
    },

    /**
     * 商务订单按状态统计数量（排除西哈努克）
     */
    countBusinessStatus() {
        return request({url: '/service/order/businessCountStatus', method: 'get'})
    },

    /** 西哈努克商务订单 Tab 统计 */
    countBusinessStatusSihanouk() {
        return request({url: '/service/order/businessCountStatusSihanouk', method: 'get'})
    },

    /**
     * 商务中心当前用户身份（管理人 / 航线负责人）
     */
    getBusinessOrderIdentity() {
        return request({url: '/service/order/getBusinessOrderIdentity', method: 'get'})
    },

    /**
     * 查询订单拒绝原因信息（轻量接口）
     * 返回 {orderNo, carrierCode, etd, rejectReason, rejectTime}
     * @param {String} id - 订单ID
     */
    queryRejectReason(id) {
        return request({url: '/service/order/rejectReason?id=' + id, method: 'get'})
    },

    /**
     * 修改订单状态
     * 支持：3(订舱中)/5(已放舱)/6(已取消)
     * 放舱(5)时可携带 containerBusinessList 按行补填供应商与成本
     * @param {Object} data - {orderId, targetStatus, soNo, cancelReason, containerBusinessList}
     */
    changeOrderStatus(data) {
        return request({url: '/service/order/changeStatus', method: 'post', data})
    },

    /**
     * 商务保存集装箱行供应商与成本（订单详情页填写，不变更状态）
     * @param {Object} data - {orderId, containerBusinessList: [{containerId, supplierId, cost}]}
     */
    updateBusinessInfo(data) {
        return request({url: '/service/order/updateBusinessInfo', method: 'post', data})
    },

    /**
     * 修改订单SO号（已放舱订单）
     * @param {Object} data - {orderId, soNo}
     */
    changeOrderSoNo(data) {
        return request({url: '/service/order/changeSoNo', method: 'post', data})
    },

    /**
     * 补充订单柜号（已放舱：我的订单/商务中心）
     * @param {Object} data - {orderId, containerNo}
     */
    changeOrderContainerNo(data) {
        return request({url: '/service/order/changeContainerNo', method: 'post', data})
    },

    /**
     * 修改订单读取状态
     * 支持：0-未读 1-已读
     * @param {Object} data - {orderId, readStatus}
     */
    changeOrderReadStatus(data) {
        return request({url: '/service/order/changeReadStatus', method: 'post', data})
    },

    /**
     * 拒绝订单（商务操作）
     * @param {Object} data - {orderId, rejectReason, recommend}
     */
    rejectOrder(data) {
        return request({url: '/service/order/reject', method: 'post', data})
    },

    /**
     * 删除订单（逻辑删除）
     * @param {String} id - 订单ID
     */
    deleteOrder(id) {
        return request({url: '/service/order/delete?id=' + id, method: 'put'})
    },

    /**
     * 我的订单 - 订单详情
     * @param {String} id - 订单ID
     */
    getOrderDetail(id) {
        return request({url: '/service/order/detail?id=' + id, method: 'get'})
    },

    /**
     * 商务中心 - 订单详情
     * @param {String} id - 订单ID
     */
    getBusinessOrderDetail(id) {
        return request({url: '/service/order/businessDetail?id=' + id, method: 'get'})
    },

    /**
     * HS编码下拉检索
     * @param {String} keyword - 搜索关键字
     */
    searchHsCodeOptions(keyword) {
        return request({
            url: '/service/order/hsCodeOptions',
            method: 'get',
            params: { keyword }
        })
    },
    /**
     * 分页查询HS Code
     */
    hsCodeList(data) {
        return request({
            url: '/service/order/hsCodeList',
            method: 'post',
            data
        })
    },

    /**
     * 预估订舱费用
     * @param {Object} data - 费用预估参数
     */
    estimateBookingFee(data) {
        return request({
            url: '/service/order/estimateBookingFee',
            method: 'post',
            data
        })
    },

    /**
     * 保存/提交订单
     * @param {Object} data - 订单数据，operationType: 1保存 2提交
     */
    saveOrder(data) {
        return request({
            url: '/service/order/save',
            method: 'post',
            data
        })
    },

    /**
     * 修改草稿订单
     * @param {Object} data - 同保存参数，额外必填 orderId
     */
    updateOrder(data) {
        return request({
            url: '/service/order/update',
            method: 'post',
            data
        })
    },

    /**
     * 商务修改订单柜型数量（订舱中/已放舱）
     * @param {Object} data - { orderId, containerList: [{containerType, quantity, weight}] }
     */
    businessUpdateContainer(data) {
        return request({
            url: '/service/order/businessUpdateContainer',
            method: 'post',
            data
        })
    },

    /**
     * 导出商务订单（按当前筛选条件 + 自选字段）
     * @param {Object} data - 同 businessList 查询条件 + exportFields: string[]
     */
    exportBusinessOrders(data) {
        return request({
            url: '/service/order/exportBusinessOrders',
            method: 'post',
            data,
            responseType: 'blob',
            timeout: 120000
        })
    },

    exportBusinessOrdersSihanouk(data) {
        return request({
            url: '/service/order/exportBusinessOrdersSihanouk',
            method: 'post',
            data,
            responseType: 'blob',
            timeout: 120000
        })
    },

    /** 我的放舱联系人列表 */
    listMyBookingContacts() {
        return request({ url: '/service/order/listMyBookingContacts', method: 'get' })
    },

    /** 修改我的放舱联系人 */
    updateBookingContact(data) {
        return request({ url: '/service/order/updateBookingContact', method: 'post', data })
    },

    /** 删除我的放舱联系人 */
    deleteBookingContact(id) {
        return request({ url: '/service/order/deleteBookingContact?id=' + id, method: 'put' })
    },

    /**
     * 批量查询船期预占箱量（已提交订单 order_status=2，仅 20GP/40HQ）
     * @param {Object} data - { items: [{ pol, pod, carrierCode, svcCode, vessel, voyage }] }
     */
    batchCountPendingBooking(data) {
        return request({ url: '/service/order/batchCountPendingBooking', method: 'post', data })
    },
}

/**
 * 港口/船司字典API模块
 */
const dictApi = {
    /**
     * 根据类型查询港口字典
     * @param {Number} type - 0:起运港, 1:目的港
     */
    queryPortDict(type) {
        return request({url: '/service/shipAddress/getPortDictByType?type=' + type, method: 'get'})
    },

    /**
     * 查询船司字典（固定pcode=carrierCode）
     * 展示字典编码(字典值)
     */
    queryCarrierDict() {
        return request({url: '/service/sysDict/queryDictByPCode?pcode=carrierCode', method: 'get'})
    },
}

export { orderApi, dictApi }
export default orderApi
